import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CONTENT_TYPES = [
  { key: "ig_caption", label: "Instagram caption" },
  { key: "ig_carousel", label: "IG carousel (5 slides)" },
  { key: "reel_script", label: "Reel script (30-45s)" },
  { key: "whatsapp_broadcast", label: "WhatsApp broadcast" },
  { key: "email_campaign", label: "Email campaign" },
  { key: "bio_snippet", label: "Bio snippet" },
];

/**
 * Iter XX.44.F — Marketing Studio.
 * Partners draft, edit, copy, or archive marketing content. NEVER
 * auto-posts. Every draft carries a grounded_ok flag surfaced in the
 * UI so the partner can see when the safety lint downgraded output.
 */
export default function MarketingStudio() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState("ig_caption");
  const [brief, setBrief] = useState("");
  const [openDraftId, setOpenDraftId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?return_to=/partner/studio"); return; }
    let alive = true;
    (async () => {
      try {
        const s = await api.get("/partners/me/studio/status");
        if (!alive) return;
        setStatus(s.data);
        if (!s.data.enabled) { setLoading(false); return; }
        const d = await api.get("/partners/me/studio/drafts");
        if (!alive) return;
        setDrafts(d.data.drafts || []);
      } catch (e) {
        if (e?.response?.status === 404) navigate("/partner/claim");
        else toast.error("Couldn't load Marketing Studio");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authLoading, user, navigate]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const r = await api.post("/partners/me/studio/drafts", {
        content_type: selectedType,
        brief: brief || null,
      });
      const d = r.data.draft;
      setDrafts(prev => [d, ...prev]);
      setOpenDraftId(d.draft_id);
      setBrief("");
      toast.success("Draft ready");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Draft failed";
      toast.error(String(msg));
    } finally {
      setGenerating(false);
    }
  }

  async function updateDraft(draftId, patch) {
    try {
      const r = await api.patch(`/partners/me/studio/drafts/${draftId}`, patch);
      setDrafts(prev => prev.map(d => d.draft_id === draftId ? r.data.draft : d));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }

  async function copyDraftText(draft) {
    const txt = renderDraftAsText(draft);
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Copied to clipboard");
      await updateDraft(draft.draft_id, { status: "copied" });
    } catch {
      toast.error("Couldn't copy");
    }
  }

  if (authLoading || loading) return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading…</div>;

  if (status && !status.enabled) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6" data-testid="studio-disabled">
        <div className="max-w-md w-full text-center">
          <div className="text-2xl font-semibold mb-2">Marketing Studio</div>
          <div className="text-neutral-400 mb-6">
            The Studio is currently in early access. It will appear here once the founder enables it for your account.
          </div>
          <Link to="/partner" className="text-emerald-400 hover:text-emerald-300">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="studio-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Link to="/partner" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="studio-back">← Back to dashboard</Link>
          <h1 className="text-3xl font-semibold mt-2">Marketing Studio</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Drafts only. Gully never posts on your behalf. Review, edit, copy — you stay in control.
          </p>
        </div>

        {/* Generator */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-8" data-testid="studio-generator">
          <div className="text-sm font-medium mb-3">Generate a new draft</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {CONTENT_TYPES.map(t => (
              <button
                key={t.key}
                onClick={() => setSelectedType(t.key)}
                className={`text-left px-3 py-2 rounded border text-sm ${selectedType === t.key ? "bg-emerald-950 border-emerald-600 text-emerald-200" : "bg-neutral-950 border-neutral-800 hover:border-neutral-700"}`}
                data-testid={`studio-type-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            value={brief}
            onChange={e => setBrief(e.target.value)}
            placeholder="Optional brief (e.g. 'promote the new Sunday batting camp', 'thank the U-14 team')"
            className="w-full bg-neutral-950 border border-neutral-800 rounded px-3 py-2 text-sm mb-3 h-20"
            data-testid="studio-brief"
            maxLength={400}
          />
          <Button onClick={handleGenerate} disabled={generating} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid="studio-generate-btn">
            {generating ? "Drafting…" : "Generate draft"}
          </Button>
        </div>

        {/* Draft library */}
        <div className="space-y-3" data-testid="studio-drafts">
          {drafts.length === 0 && (
            <div className="text-sm text-neutral-500 text-center py-8" data-testid="studio-empty">
              No drafts yet. Pick a type above and generate your first one.
            </div>
          )}
          {drafts.map(d => (
            <DraftCard
              key={d.draft_id}
              draft={d}
              open={openDraftId === d.draft_id}
              onToggle={() => setOpenDraftId(v => v === d.draft_id ? null : d.draft_id)}
              onCopy={() => copyDraftText(d)}
              onArchive={() => updateDraft(d.draft_id, { status: "archived" })}
              onShared={() => updateDraft(d.draft_id, { status: "shared" })}
            />
          ))}
        </div>

        <div className="text-xs text-neutral-600 mt-10">
          All drafts are grounded in your own data. Gully never posts, sends, or shares anything on your behalf.
        </div>
      </div>
    </div>
  );
}

function DraftCard({ draft, open, onToggle, onCopy, onArchive, onShared }) {
  const c = draft.content || {};
  const label = CONTENT_TYPES.find(t => t.key === draft.content_type)?.label || draft.content_type;
  const groundingWarn = !draft.grounding_ok;
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden" data-testid={`studio-draft-${draft.draft_id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-800/40 text-left" data-testid={`studio-draft-toggle-${draft.draft_id}`}>
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-neutral-500">
            {draft.status} · {new Date(draft.created_at).toLocaleString()}
            {groundingWarn && <span className="ml-2 text-amber-400">· deterministic fallback</span>}
          </div>
        </div>
        <div className="text-neutral-500 text-xs">{open ? "▲" : "▼"}</div>
      </button>
      {open && (
        <div className="border-t border-neutral-800 p-4 space-y-3">
          <pre className="text-sm text-neutral-200 whitespace-pre-wrap font-sans bg-neutral-950 rounded p-3 border border-neutral-800" data-testid={`studio-draft-body-${draft.draft_id}`}>
{renderDraftAsText(draft)}
          </pre>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onCopy} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid={`studio-copy-${draft.draft_id}`}>Copy text</Button>
            <Button size="sm" variant="outline" onClick={onShared} className="border-neutral-700" data-testid={`studio-mark-shared-${draft.draft_id}`}>Mark as shared</Button>
            <Button size="sm" variant="outline" onClick={onArchive} className="border-red-800/60 text-red-400" data-testid={`studio-archive-${draft.draft_id}`}>Archive</Button>
          </div>
          {draft.warnings && draft.warnings.length > 0 && (
            <div className="text-xs text-amber-400" data-testid={`studio-warnings-${draft.draft_id}`}>
              Notes: {draft.warnings.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function renderDraftAsText(draft) {
  const c = draft.content || {};
  switch (draft.content_type) {
    case "ig_caption":
      return c.caption || "";
    case "ig_carousel":
      return (c.slides || []).map((s, i) => `Slide ${i + 1}: ${s.title}\n${s.body}`).join("\n\n");
    case "reel_script":
      return (c.beats || []).map(b => `[${b.timecode}] VO: ${b.voiceover}\n         Screen: ${b.on_screen || ""}`).join("\n\n");
    case "whatsapp_broadcast":
      return c.message || "";
    case "email_campaign":
      return `Subject: ${c.subject || ""}\n\n${c.body || ""}`;
    case "bio_snippet":
      return `Instagram: ${c.instagram || ""}\nLinkedIn: ${c.linkedin || ""}\nX: ${c.x || ""}`;
    default:
      return JSON.stringify(c, null, 2);
  }
}
